import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import { EventInput } from '@/types'

import { addGlobalLoadingAnimation } from '@/backend'
import { useCreateEvent } from '@/services/events'
import { useCurrentUser } from '@/services/users'

import { DateRangeField, formFor, SubmitButton } from '@/libraries/forms'
import { EventRegistrationSystemSelector } from '@/components/event/EventRegistrationSystemSelector'
import { Page } from '@/components/Page'
import { EventGrantsEditor } from '@/components/rights/EventGrantsEditor'
import { useT } from '@/i18n'
import randomId from '@/utils/randomId'

const {
  Form,
  Input,
  Field,
} = formFor<EventInput>()

export const Route = createFileRoute('/events/new')({
  component: CreateEventForm,
  staticData: {
    requireRights: 'events:create',
    breadcrumb: 'routes.events.new.newEventBreadcrumb',
  },
})

function CreateEventForm() {
  const t = useT('routes.events.new')
  const label = useT('domain.event')
  const navigate = Route.useNavigate()
  const currentUser = useCurrentUser()
  const [createEvent] = useCreateEvent({
    onCompleted: (data) => navigate({ to: '/events/$eventId/{-$eventVersionId}', params: { eventId: data.createEvent._id } }),
    refetchQueries: ['getEvents'],
  })

  const initialGrants = currentUser
    ? [{ _id: randomId(), principal: `user:${currentUser._id}`, role: 'organizer' as const }]
    : []

  const [event, setEvent] = useState<EventInput>({
    name: '',
    beginDate: '',
    endDate: '',
    accessControl: { viewAccess: 'public', grants: initialGrants },
    eventRegistrationSystem: 'None',
  })

  return <Page title={t('newEvent')}>
    <Form
      labelStyle="above"
      value={event}
      onChange={setEvent}
      onSubmit={() => addGlobalLoadingAnimation(createEvent({ event }))}
      errorDisplay="onSubmit">
      <div className="flex gap-3">
        <Input label={label('name')} path="name" required containerClassName="w-60" />
        <DateRangeField<EventInput>
          id="eventDate"
          label={label('eventDate')}
          beginPath="beginDate"
          endPath="endDate"
          required
        />
      </div>
      <Field
        label={label('eventRegistrationSystem')}
        path="eventRegistrationSystem"
        component={EventRegistrationSystemSelector}
      />
      <EventGrantsEditor />
      <SubmitButton text={t('create')} />
    </Form>
  </Page>
}
